import {
  useLazyContext,
  useRequiredContext,
} from "@redotech/react-util/context";
import { useTranslation } from "@redotech/redo-i18n";
import { RuleTypeDetailsProps } from "@redotech/redo-return-ui/flows/rules/multiple-actions";
import { ReceivedAtEmailState } from "@redotech/redo-return-ui/flows/rules/received-at-email";
import { RedoFilterDropdownAnchor } from "@redotech/redo-web/arbiter-components/filter-group/redo-filter-group";
import { RedoMultiselectDropdown } from "@redotech/redo-web/arbiter-components/list/redo-multiselect-dropdown";
import { TeamContext } from "@redotech/redo-web/context/team-context";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useEffect, useMemo, useState } from "react";
import { EmailIntegrationsContext } from "../../services/email-integrations";
import * as conditionsCss from "./conditions.module.css";

export const ReceivedAtEmailDetails = memo(function ReceivedAtEmailDetails({
  state,
  setState,
  disabled,
}: RuleTypeDetailsProps<ReceivedAtEmailState>) {
  const { t } = useTranslation();
  const [availableEmails, setAvailableEmails] = useState<string[]>([]);

  const [emailIntegrationsLoad] = useLazyContext(EmailIntegrationsContext);
  const team = useRequiredContext(TeamContext);

  useEffect(() => {
    if (!emailIntegrationsLoad.value) {
      return;
    }

    const emails = [
      ...(emailIntegrationsLoad.value.customIntegrations || []),
      ...(emailIntegrationsLoad.value.gmailIntegrations || []),
      ...(emailIntegrationsLoad.value.outlookIntegrations || []),
    ].map((integration) => integration.email);

    // Add default email if configured and not already in the list
    const defaultEmail = team.settings?.support?.defaultEmail;
    if (defaultEmail && !emails.includes(defaultEmail)) {
      emails.push(defaultEmail);
    }

    appendEmails(emails);
  }, [emailIntegrationsLoad.value, team]);

  const appendEmails = (emails: string[]) => {
    setAvailableEmails((oldAvailableEmails: string[]) => {
      const updatedAvailableEmails = oldAvailableEmails;
      for (const email of emails) {
        if (!updatedAvailableEmails.includes(email)) {
          updatedAvailableEmails.push(email);
        }
      }
      return [...updatedAvailableEmails];
    });
  };

  const [dropdownButtonRef, setDropdownButtonRef] =
    useState<HTMLButtonElement | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const emailItems = useMemo(() => {
    const emails = availableEmails.length
      ? availableEmails
      : [t("No email addresses configured")];
    return emails.map((email) => ({
      id: email,
      type: "text" as const,
      text: email,
      value: email,
    }));
  }, [availableEmails, t]);

  const selectedItems = useMemo(() => {
    return state.map((email) => ({
      id: email,
      type: "text" as const,
      text: email,
      value: email,
    }));
  }, [state]);

  const displayText =
    state.length > 0
      ? state.length === 1
        ? state[0]
        : t("{{count}} emails selected", { count: state.length })
      : t("Select email addresses");

  const isDisabled = disabled || availableEmails.length === 0;

  return (
    <Flex dir="column" gap="sm" w="full">
      <Text fontSize="xs" textColor="tertiary">
        {t("Email Address")}
      </Text>
      <RedoFilterDropdownAnchor
        className={conditionsCss.fullWidthButton}
        color={isDisabled ? "secondary" : "primary"}
        onClick={() => {
          if (!isDisabled) {
            setDropdownOpen(!dropdownOpen);
          }
        }}
        ref={setDropdownButtonRef}
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        text={displayText}
      />
      <RedoMultiselectDropdown
        dropdownAnchor={dropdownButtonRef}
        dropdownOpen={dropdownOpen}
        fitToAnchor
        items={emailItems}
        selectedItems={selectedItems}
        selectionVariant="checkbox"
        setDropdownOpen={setDropdownOpen}
        setSelectedItems={(items) => {
          setState(items.map((item) => item.value));
        }}
        size="xs"
      />
    </Flex>
  );
});
